import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../services/api';

const Transparency = () => {
  const [tabValue, setTabValue] = useState(0);
  const [summaryData, setSummaryData] = useState({
    totalDonations: 0,
    totalExpenses: 0,
    balance: 0,
    monthlyDonations: 0,
    monthlyExpenses: 0,
  });
  const [donations, setDonations] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [summaryRes, donationsRes] = await Promise.all([
          api.get('/finance/summary'),
          api.get('/finance/donations', { params: { limit: 100 } }),
        ]);

        const data = summaryRes.data?.data || {};
        setSummaryData({
          totalDonations: data.totalDonations || 0,
          totalExpenses: data.totalExpenses || 0,
          balance: data.balance || 0,
          monthlyDonations: data.monthlyDonations || 0,
          monthlyExpenses: data.monthlyExpenses || 0,
        });

        setDonations(donationsRes.data?.data || []);
      } catch (error) {
        console.error('Error loading finance data:', error);
      }
    };

    fetchData();
  }, []);

  const expenses = [
    { id: 1, date: '2024-01-16', amount: 50000, category: 'Education Programs', description: 'Scholarship distribution' },
    { id: 2, date: '2024-01-25', amount: 30000, category: 'Women Empowerment', description: 'Workshop expenses' },
    { id: 3, date: '2024-02-05', amount: 20000, category: 'Operations', description: 'Office maintenance' },
  ];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDownloadReport = (type) => {
    // Implement download functionality
    console.log(`Downloading ${type} report...`);
  };

  return (
    <Box sx={{ minHeight: '80vh', py: 8, backgroundColor: '#f9fafb' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          component="h1"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: 2,
            color: '#f97316',
          }}
        >
          Transparency Dashboard
        </Typography>
        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
            mb: 6,
            maxWidth: '800px',
            mx: 'auto',
          }}
        >
          Complete financial transparency - Track all donations and expenses
        </Typography>

        {/* Summary Cards */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AccountBalanceIcon sx={{ fontSize: 40, color: '#f97316', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                    Total Donations
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f97316' }}>
                  ₹{summaryData.totalDonations.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUpIcon sx={{ fontSize: 40, color: '#059669', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                    Total Expenses
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#059669' }}>
                  ₹{summaryData.totalExpenses.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AccountBalanceIcon sx={{ fontSize: 40, color: '#f97316', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                    Current Balance
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f97316' }}>
                  ₹{summaryData.balance.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  This Month
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827', mb: 1 }}>
                  Donations: ₹{summaryData.monthlyDonations.toLocaleString()}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#059669' }}>
                  Expenses: ₹{summaryData.monthlyExpenses.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
              },
            }}
          >
            <Tab label="Donations" />
            <Tab label="Expenses" />
            <Tab label="Project-wise Utilization" />
            <Tab label="Financial Reports" />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                  All Donations
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadReport('donations')}
                  sx={{ borderColor: '#f97316', color: '#f97316' }}
                >
                  Download Report
                </Button>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Donor</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {donations.map((donation) => (
                      <TableRow key={donation.id}>
                        <TableCell>
                          {new Date(donation.date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{donation.donorName}</TableCell>
                        <TableCell>{donation.purpose}</TableCell>
                        <TableCell align="right">
                          ₹{donation.amount.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {tabValue === 1 && (
          <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                  All Expenses
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadReport('expenses')}
                  sx={{ borderColor: '#f97316', color: '#f97316' }}
                >
                  Download Report
                </Button>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expenses.map((expense) => (
                      <TableRow key={expense.id}>
                        <TableCell>{new Date(expense.date).toLocaleDateString()}</TableCell>
                        <TableCell>{expense.category}</TableCell>
                        <TableCell>{expense.description}</TableCell>
                        <TableCell align="right">₹{expense.amount.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {tabValue === 2 && (
          <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#111827' }}>
                Project-wise Donation Utilization
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#111827' }}>
                      Education Programs
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Total Allocated: ₹300,000
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Utilized: ₹250,000 (83%)
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#111827' }}>
                      Women Empowerment
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Total Allocated: ₹200,000
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Utilized: ₹150,000 (75%)
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#111827' }}>
                      Youth Mentorship
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Total Allocated: ₹150,000
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Utilized: ₹100,000 (67%)
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#111827' }}>
                      Culture & Sanskriti
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Total Allocated: ₹100,000
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Utilized: ₹75,000 (75%)
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {tabValue === 3 && (
          <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#111827' }}>
                Financial Reports & Documents
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" sx={{ p: 2, cursor: 'pointer', '&:hover': { borderColor: '#f97316' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <ReceiptIcon sx={{ fontSize: 32, color: '#f97316', mr: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                        Monthly Report
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      January 2024
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      sx={{ borderColor: '#f97316', color: '#f97316' }}
                    >
                      Download
                    </Button>
                  </Card>
                </Grid>
                {/* Add more report cards here */}
              </Grid>
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default Transparency;

